import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { MaskedHeading, Fade, FadeUp, ImageReveal, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CaseCard from "@/components/CaseCard";
import { useContent } from "@/context/ContentContext";
import { getCases } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

const USPS = [
  { n: "01", t: "Rådgivning ud fra dit hus", d: "Vi ser på boligens arkitektur, lysindfald og dine behov, før vi anbefaler en løsning. Derfor får du ikke bare et tilbud, men et kvalificeret grundlag for at vælge rigtigt." },
  { n: "02", t: "Vi laver ikke alt — vi kan døre og vinduer", d: "Vores specialisering betyder, at vi kan rådgive mere kvalificeret og har blik for de detaljer, der afgør, om løsningen både fungerer og ser rigtig ud." },
  { n: "03", t: "Helheden skal hænge sammen", d: "Materialer, proportioner, funktion og udførelse tænkes sammen fra den første rådgivning til den sidste finish. Så resultatet passer naturligt til huset og holder mange år frem." },
];

export default function Home() {
  const { hero } = useContent();
  const [cases, setCases] = useState([]);
  const heroRef = useRef(null);

  usePageMeta({
    title: null, // default site title
    description:
      "Hagelund er specialister i døre og vinduer. Vi rådgiver ud fra dit hus — ikke ud fra, hvad der er nemmest at sælge. Book et uforpligtende inspirationsmøde.",
    path: "/",
  });

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.7]);

  useEffect(() => {
    getCases().then(setCases).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -top-[10%] h-[120%]">
          <img src="/images/hero.webp" alt="Hagelund vinduer og døre" className="img-cover" style={{ objectPosition: "center 40%" }} />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/25" />

        <div className="relative h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-center pt-24 pb-16 md:pb-20">
          <Fade delay={0.2}>
            <p className="text-white/80 text-xs uppercase tracking-label mb-6">{hero.eyebrow}</p>
          </Fade>
          <h1 className="font-display text-white font-light leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] xl:text-[5.25rem] tracking-tight">
            <MaskedHeading lines={[hero.line1, hero.line2, hero.line3]} delay={0.35} />
          </h1>
          <Fade delay={1.2}>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl">
              <p className="text-white/85 font-light text-base md:text-lg max-w-xl">{hero.intro}</p>
            </div>
          </Fade>
          <Fade delay={1.4}>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                data-testid="hero-cta-book"
                data-track="cta_click"
                className="group inline-flex items-center gap-3 bg-background text-foreground px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {hero.cta} <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/cases"
                data-testid="hero-cta-cases"
                data-track="cta_click"
                className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-white hover:text-foreground transition-colors"
              >
                Se vores cases
              </Link>
            </div>
          </Fade>
        </div>
      </section>

      <Marquee />

      {/* POSITIONING */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-40">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-4">
            <FadeUp>
              <p className="text-xs uppercase tracking-label text-accent">Vores tilgang</p>
            </FadeUp>
          </div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="font-display text-4xl md:text-6xl font-light leading-[1.05] text-balance">
                Døre og vinduer skal ikke bare passe i målene. De skal passe til huset.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-10 text-muted-foreground font-light text-lg max-w-2xl">
                De rigtige døre og vinduer kan gøre en mærkbar forskel for både husets udtryk, lysindfald, komfort og energiforbrug. Derfor ser vi på helheden, før vi anbefaler en løsning. Vi rådgiver om materialer, proportioner, funktion og æstetik, så resultatet fungerer i hverdagen, respekterer husets arkitektur og skaber værdi mange år frem.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-24 md:pb-40">
        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {[
            { to: "/vinduer", label: "Vinduer", img: "/images/skovvej-8.webp", text: "Mere dagslys, bedre komfort og en løsning, der respekterer husets arkitektur." },
            { to: "/doere", label: "Døre", img: "/images/skovvej-15.webp", text: "Den rette dør skaber sammenhæng mellem husets udtryk, funktion og ankomst." },
          ].map((s, i) => (
            <FadeUp key={s.to} delay={i * 0.1}>
              <Link to={s.to} className="group block" data-testid={`service-${s.label.toLowerCase()}`}>
                <div className="overflow-hidden aspect-[16/11] bg-muted">
                  <img src={s.img} alt={s.label} className="img-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                </div>
                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <h3 className="font-display text-5xl md:text-6xl font-light">{s.label}</h3>
                    <p className="mt-2 text-muted-foreground font-light">{s.text}</p>
                  </div>
                  <ArrowUpRight size={32} weight="light" className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* MANIFESTO / USP */}
      <section className="bg-foreground text-background py-24 md:py-40">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-label text-background/50 mb-16">Det får du hos Hagelund</p>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-12 md:gap-10">
            {USPS.map((u, i) => (
              <FadeUp key={u.n} delay={i * 0.12}>
                <div className="border-t border-background/20 pt-8">
                  <span className="font-display text-6xl md:text-7xl font-light text-background/30">{u.n}</span>
                  <h3 className="font-display text-3xl md:text-4xl font-light mt-6 leading-tight">{u.t}</h3>
                  <p className="mt-4 text-background/60 font-light">{u.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASES */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-40">
        <div className="flex items-end justify-between mb-16">
          <FadeUp>
            <p className="text-xs uppercase tracking-label text-accent mb-4">Udvalgte projekter</p>
            <h2 className="font-display text-4xl md:text-6xl font-light">Inspirationsunivers</h2>
          </FadeUp>
          <FadeUp>
            <Link to="/cases" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-label border-b border-foreground/30 pb-1 hover:border-foreground" data-testid="cases-see-all">
              Alle cases <ArrowRight size={14} weight="bold" />
            </Link>
          </FadeUp>
        </div>
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {cases.map((c, i) => (
            <CaseCard key={c.id} item={c} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <ImageReveal src="/images/skovvej-6.webp" alt="" className="absolute inset-0 h-full" imgClassName="h-full" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-12 py-28 md:py-44 text-center">
          <FadeUp>
            <p className="text-xs uppercase tracking-label text-white/70 mb-6">Kom godt i gang</p>
            <h2 className="font-display text-white text-5xl md:text-7xl font-light leading-none max-w-4xl mx-auto text-balance">
              Book et uforpligtende inspirationsmøde
            </h2>
            <Link
              to="/kontakt"
              data-testid="cta-book"
              className="group inline-flex items-center gap-3 bg-background text-foreground mt-10 px-10 py-5 text-xs uppercase tracking-label font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
